import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigation } from '../contexts/NavigationContext';
import { Mail, User, Loader, CheckCircle, Phone, MapPin } from 'lucide-react';
import { GoogleAddressAutocomplete, AddressDetails } from './common/GoogleAddressAutocomplete';
import { Address } from '../types';

export const MagicLink: React.FC = () => {
  const { refreshUser } = useAuth();
  const { navigate } = useNavigation();
  const [token, setToken] = useState('');
  const [needsSetup, setNeedsSetup] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState<Address>({});
  const [addressFormatted, setAddressFormatted] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [userId, setUserId] = useState('');
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  useEffect(() => {
    // Get token and flags from URL query params
    const urlParams = new URLSearchParams(window.location.search);
    const tokenParam = urlParams.get('token');
    const needsSetupParam = urlParams.get('needsSetup') === 'true';
    const requires2FAParam = urlParams.get('requires2FA') === 'true';
    const needs2FASetupParam = urlParams.get('needs2FASetup') === 'true';

    if (tokenParam) {
      setToken(tokenParam);
      setNeedsSetup(needsSetupParam);
      
      // Decode token to get user info
      let userEmail = '';
      try {
        const payload = JSON.parse(atob(tokenParam.split('.')[1]));
        userEmail = payload.email || '';
        setUserEmail(userEmail);
        setUserId(payload.id);
      } catch (error) {
        console.error('Error decoding token:', error);
      }

      // If 2FA setup is needed, redirect to 2FA setup page
      if (needs2FASetupParam) {
        localStorage.setItem('token', tokenParam);
        window.location.href = '/auth/setup-2fa';
        return;
      }

      // If 2FA is required, redirect to login with 2FA prompt
      if (requires2FAParam) {
        localStorage.setItem('token', tokenParam);
        window.location.href = `/auth/login?email=${encodeURIComponent(userEmail)}&requires2FA=true`;
        return;
      }

      // Auto-login with the token
      handleAutoLogin(tokenParam);
    } else {
      setMessage('Invalid magic link. No token found.');
      setIsLoading(false);
    }
  }, []);

  const handleAutoLogin = async (sessionToken: string) => {
    try {
      // Store token first
      localStorage.setItem('token', sessionToken);
      
      // Fetch user data to verify token and get user info
      const response = await fetch(`${apiUrl}/api/auth/me`, {
        headers: {
          'Authorization': `Bearer ${sessionToken}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to authenticate');
      }

      const data = await response.json();
      
      if (data.success && data.user) {
        const user = data.user;
        
        // Check if user needs 2FA setup (first time)
        if (user.needs2FASetup) {
          // Store token and redirect to 2FA setup
          setIsLoading(false);
          navigate('/auth/setup-2fa');
          return;
        }

        // Check if 2FA is required (enabled but not verified in last 30 days)
        const requires2FA = user.twoFactorEnabled && (!user.last2FAVerification || 
          (new Date().getTime() - new Date(user.last2FAVerification).getTime()) > (30 * 24 * 60 * 60 * 1000));

        // If 2FA is required, redirect to login page with 2FA prompt
        if (requires2FA) {
          setIsLoading(false);
          navigate(`/auth/login?email=${encodeURIComponent(user.email)}&requires2FA=true`);
          return;
        }

        // Store user data
        localStorage.setItem('user', JSON.stringify(user));
        
        // Refresh user context to update AuthContext
        await refreshUser();

        // Small delay to ensure context is updated
        await new Promise(resolve => setTimeout(resolve, 100));

        // Always show profile setup for first-time users (when needsSetup is true)
        // For returning users, check if profile is incomplete
        const isFirstTime = needsSetup || !user.firstName || !user.lastName || 
                            user.firstName === user.email.split('@')[0] || 
                            user.lastName === 'User';
        
        if (isFirstTime) {
          setFirstName(user.firstName || '');
          setLastName(user.lastName || '');
          setPhone(user.phone || user.smsPhoneNumber || '');
          if (user.address) {
            setAddress(user.address);
            setAddressFormatted(user.address.formatted || '');
          }
          setNeedsSetup(true); // Force show setup form
          setIsLoading(false);
        } else {
          // User is logged in and profile is complete, redirect to dashboard immediately
          setIsLoading(false);
          navigate('/');
        }
      } else {
        setMessage('Failed to authenticate. Please try again.');
        setIsLoading(false);
      }
    } catch (error) {
      console.error('Auto-login error:', error);
      setMessage('Failed to authenticate. Please try again.');
      setIsLoading(false);
    }
  };

  const handleAddressSelect = (addressDetails: AddressDetails) => {
    setAddress({
      formatted: addressDetails.formatted,
      streetNumber: addressDetails.streetNumber,
      streetName: addressDetails.streetName,
      street: addressDetails.street,
      city: addressDetails.city,
      state: addressDetails.state,
      postalCode: addressDetails.postalCode,
      country: addressDetails.country,
      lat: addressDetails.lat,
      lng: addressDetails.lng
    });
    setAddressFormatted(addressDetails.formatted);
  };

  const handleSaveSetup = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    
    // Name is required
    if (!firstName || !lastName) {
      setMessage('First name and last name are required.');
      return;
    }

    setIsSaving(true);
    setMessage('');

    if (!userId) {
      setMessage('User ID not found. Please try again.');
      setIsSaving(false);
      return;
    }

    try {
      const payload: any = { 
        firstName, 
        lastName 
      };
      
      // Only include phone if provided
      if (phone) {
        payload.phone = phone;
        payload.smsPhoneNumber = phone; // Also set SMS phone number
      }
      
      // Only include address if any address fields are provided
      if (addressFormatted || Object.keys(address).length > 0) {
        payload.address = address;
      }

      const response = await fetch(`${apiUrl}/api/users/${userId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();
      
      if (data.success) {
        // Refresh user data from server
        await refreshUser();
        
        setMessage('Profile updated! Redirecting...');
        
        // Check if user needs 2FA setup
        const userResponse = await fetch(`${apiUrl}/api/auth/me`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        const userData = await userResponse.json();
        
        // Redirect to 2FA setup if needed, otherwise to dashboard
        setTimeout(() => {
          if (userData.success && userData.user?.needs2FASetup) {
            navigate('/auth/setup-2fa');
          } else {
            navigate('/');
          }
        }, 1000);
      } else {
        setMessage(data.message || 'Failed to update profile');
      }
    } catch (error) {
      console.error('Save setup error:', error);
      setMessage('Failed to update profile. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSkip = async () => {
    // Save minimal required info (name) if not already set
    if (!firstName || !lastName) {
      // Try to get name from email if not provided
      const emailPrefix = userEmail.split('@')[0];
      setFirstName(emailPrefix);
      setLastName('User');
    }
    
    // Save and proceed
    await handleSaveSetup();
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center px-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg p-8 shadow-lg max-w-md w-full text-center">
          <Loader className="w-16 h-16 mx-auto mb-4 text-primary-600 animate-spin" />
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">Logging you in...</h2>
          <p className="text-gray-600 dark:text-gray-400">Please wait while we set up your account.</p>
        </div>
      </div>
    );
  }

  if (needsSetup) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center px-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg p-8 shadow-lg max-w-lg w-full">
          <div className="text-center mb-8">
            <div className="text-primary-600 mb-4">
              <CheckCircle className="w-16 h-16 mx-auto" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">Welcome!</h2>
            {userEmail && (
              <p className="text-gray-600 dark:text-gray-400">
                You're logged in as <strong>{userEmail}</strong>
              </p>
            )}
            <p className="text-gray-600 dark:text-gray-400 mt-2">
              Let's finish setting up your profile. You can skip optional fields for now.
            </p>
          </div>

          <form onSubmit={handleSaveSetup} className="space-y-4">
            {/* Email (read-only) */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Email
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="email"
                  id="email"
                  value={userEmail}
                  disabled
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 cursor-not-allowed"
                />
              </div>
            </div>

            <div>
              <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                First Name <span className="text-error-500">*</span>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <User className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  id="firstName"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
                  placeholder="Enter your first name"
                  required
                />
              </div>
            </div>

            <div>
              <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Last Name <span className="text-error-500">*</span>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <User className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  id="lastName"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
                  placeholder="Enter your last name"
                  required
                />
              </div>
            </div>

            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Phone Number <span className="text-gray-500 text-xs">(Optional)</span>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Phone className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="tel"
                  id="phone"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
                  placeholder="Enter your phone number"
                />
              </div>
            </div>

            <div>
              <label htmlFor="address" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Home Address <span className="text-gray-500 text-xs">(Optional)</span>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none z-10">
                  <MapPin className="h-5 w-5 text-gray-400" />
                </div>
                <GoogleAddressAutocomplete
                  value={addressFormatted}
                  onChange={(value) => {
                    setAddressFormatted(value);
                    if (!value) {
                      setAddress({});
                    }
                  }}
                  onPlaceSelect={handleAddressSelect}
                  placeholder="Enter your home address"
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
                />
              </div>
            </div>

            {message && (
              <div className={`p-3 rounded-md text-sm ${
                message.includes('success') || message.includes('Redirecting')
                  ? 'bg-success-100 text-success-700 dark:bg-success-900 dark:text-success-300'
                  : 'bg-error-100 text-error-700 dark:bg-error-900 dark:text-error-300'
              }`}>
                {message}
              </div>
            )}

            <div className="flex gap-3">
              <button
                type="button"
                onClick={handleSkip}
                disabled={isSaving}
                className="flex-1 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 py-2 px-4 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:opacity-50"
              >
                Skip for Now
              </button>
              <button
                type="submit"
                disabled={isSaving || !firstName || !lastName}
                className="flex-1 bg-primary-600 text-white py-2 px-4 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 flex items-center justify-center space-x-2"
              >
                {isSaving ? (
                  <>
                    <Loader className="w-5 h-5 animate-spin" />
                    <span>Saving...</span>
                  </>
                ) : (
                  <span>Complete Setup</span>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  // Should not reach here, but just in case
  return null;
};

